// The roles a piece can carry in Godsmode. A role is on the piece, not on
// its kind: a bishop carries a caster at the start, a rook holds energy, and
// a knight gives a pitch, and each keeps its role when its kind changes. A
// copy of a piece is a copy of its role. Rob, 2026-10-13: "These assignments
// should be treated as starting conventions, rather than hard and fast
// constraints", and "Casters are default assigned to bishops, but casting is
// not a property of bishops."

import type { Board, Piece, Pitch, Side } from './types';

/** The casters that a living piece of the side carries. A caster with no such piece is gone. */
export const livingCasterIds = (board: Board, side: Side): Set<string> => {
  const ids = new Set<string>();
  for (const piece of board) {
    if (piece?.owner === side && piece.casterId !== null) ids.add(piece.casterId);
  }
  return ids;
};

/** The pitches that a living piece of the side gives. */
export const livingPitches = (board: Board, side: Side): Set<Pitch> => {
  const pitches = new Set<Pitch>();
  for (const piece of board) {
    if (piece?.owner === side && piece.pitch !== null) pitches.add(piece.pitch);
  }
  return pitches;
};

/** The energy the pieces of the side hold, over all of them. */
export const boardEnergy = (board: Board, side: Side): number =>
  board.reduce((sum, piece) => (piece?.owner === side ? sum + piece.energy : sum), 0);

/** The most energy the pieces of the side can hold. With no piece that holds energy it is zero. */
export const energyCapacity = (board: Board, side: Side): number =>
  board.reduce((sum, piece) => (piece?.owner === side ? sum + piece.energyCap : sum), 0);

/** The square of the piece of the side that `prefer` ranks first, among those `allow` lets in. */
const pickHolder = (
  board: Board,
  side: Side,
  allow: (piece: Piece) => boolean,
  prefer: (a: Piece, b: Piece) => number,
): number | null => {
  let best: number | null = null;
  board.forEach((piece, square) => {
    if (piece?.owner !== side || !allow(piece)) return;
    const current = best === null ? null : board[best];
    if (!current || prefer(piece, current) < 0) best = square;
  });
  return best;
};

const withEnergy = (board: Board, square: number, change: number): Board =>
  board.map((piece, at) =>
    piece && at === square ? { ...piece, energy: piece.energy + change } : piece,
  );

/**
 * Gives energy to the pieces of the side that have room, and says how much
 * found room. Rob, 2026-10-13: "Energy is automatically distributed across
 * the rooks, one point at a time, emptiest first." Energy with no room is
 * lost.
 */
export const addEnergy = (
  board: Board,
  side: Side,
  amount: number,
): { board: Board; stored: number } => {
  let next = board;
  let stored = 0;
  while (stored < amount) {
    const square = pickHolder(
      next,
      side,
      (piece) => piece.energy < piece.energyCap,
      (a, b) => a.energy - b.energy,
    );
    if (square === null) break;
    next = withEnergy(next, square, 1);
    stored += 1;
  }
  return { board: next, stored };
};

/**
 * Takes energy from the pieces of the side. Rob, 2026-10-13: "Energy is spent
 * one point at a time, always from the fullest available rook." The caller
 * has checked that the side holds that much.
 */
export const spendEnergy = (board: Board, side: Side, amount: number): Board => {
  let next = board;
  for (let spent = 0; spent < amount; spent += 1) {
    const square = pickHolder(
      next,
      side,
      (piece) => piece.energy > 0,
      (a, b) => b.energy - a.energy,
    );
    if (square === null) break;
    next = withEnergy(next, square, -1);
  }
  return next;
};

/** A string that changes when a role of the piece does. The board redraw reads it. */
export const roleSignature = (piece: Piece): string =>
  [piece.casterId, piece.energy, piece.energyCap, piece.pitch].join(',');
